import { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  fetchCollectionAllocations,
  voidCollectionReceipt,
} from '#/api/crmMgmt/customerOrder/collection';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import {
  buildReceiptVoidPayload,
  localDateTimeToApi,
  nowLocalDateTime,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { formatDisplayAmount } from '#/utils/formatNumber';

import { collectionQueryKey } from './collectionQueryKeys';
import useCollectionMutation from './useCollectionMutation';

/**
 * 收款沖銷（PAYMENT_VOID）：全額，系統帶出該筆全部期別抵付與子單分配作反向列。
 * 有未沖銷退款時後端會拒絕，需先處理退款。
 */
const CollectionReceiptVoidDialog = ({ open, onOpenChange, customerOrderId, receipt }) => {
  const [occurredAt, setOccurredAt] = useState(nowLocalDateTime);
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState(null);

  const allocationsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'allocations'),
    queryFn: () => fetchCollectionAllocations(customerOrderId),
    enabled: open,
  });

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => voidCollectionReceipt(
      customerOrderId,
      receipt.id,
      payload,
      { idempotencyKey },
    ),
    { successMessage: '已沖銷收款', onSuccess: () => onOpenChange(false) },
  );

  const preview = buildReceiptVoidPayload({
    receipt,
    allocations: allocationsQuery.data?.data ?? [],
    actualOccurredAt: null,
    reason: '',
  });

  function handleConfirm() {
    const actualOccurredAt = localDateTimeToApi(occurredAt);
    let error = null;
    if (!reason.trim()) {
      error = '請填寫沖銷原因';
    } else if (!actualOccurredAt) {
      error = '請填寫沖銷時間';
    } else if (!allocationsQuery.isSuccess) {
      error = '子單分配尚未載入，請稍後再試';
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate(buildReceiptVoidPayload({
      receipt,
      allocations: allocationsQuery.data?.data ?? [],
      actualOccurredAt,
      reason: reason.trim(),
    }));
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="沖銷收款"
      description="用於登錄錯誤的收款，會全額反向且保留原紀錄；若是實際把錢退給客戶，請改用「登錄退款」。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '沖銷中…' : '沖銷'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5 text-sm">
        <p>
          沖銷金額 {formatDisplayAmount(preview.amount)}；
          反向期別抵付 {preview.phaseReversals.length} 筆、子單分配 {preview.salesReversals.length} 筆。
        </p>
        <FloatingInput
          id="collection-void-occurred-at"
          type="datetime-local"
          label="沖銷時間"
          value={occurredAt}
          onChange={(event) => setOccurredAt(event.target.value)}
        />
        <FloatingTextarea
          id="collection-void-reason"
          label="沖銷原因"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
        {formError ? <p className="text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionReceiptVoidDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  receipt: PropTypes.object.isRequired,
};

export default CollectionReceiptVoidDialog;
